import {
    Box,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Text,
    Badge,
} from "@chakra-ui/react";

interface PrProcessTableProps {
    data: any[];
}

const columnMapping: Record<string, string> = {
    REQ_ID: "Request ID",
    BANFN: "PR Number",
    MANDT: "Client",
    SAVE_DATE: "Save Date",
    SAVE_TIME: "Save Time",
    PLANT: "Plant",
    BNFPO: "Item No",
    PERNR: "Personnel No",
    EMP_FNAME: "Employee Name",
    OWNERID: "Owner ID",
    ERNAM: "Created By",
    ERDAT: "Creation Date",
    MATNR: "Material No",
    TXZ01: "Description",
    MENGE: "Quantity",
    MEINS: "UOM",
    WAERS: "Currency",
    EKGRP: "Purch. Group",
    TOTAL_VALUE: "Total Value",
    TOTAL_PR_VALUE: "Total PR Value",
    PREIS: "Price",
    TCODE: "T-Code",
    GL_ACCOUNT: "G/L Account",
    GL_DESC: "G/L Description",
    COST_CENTER: "Cost Center",
    CC_DESC: "Cost Center Desc",
    IN_ORDER: "Internal Order",
    PROJECT_NAME: "Project Name",
    GROUP_LEADER: "Group Leader",
    CONV_RATE: "Conv. Rate",
    FLAG: "Flag",
    STATUS: "Status",
    LOEKZ: "Deletion Ind.",
    LBLNI: "Entry Sheet No",
    LBLNE: "External No",
    SRVPOS: "Service No",
    EBELN: "PO Number",
    EBELP: "PO Item No",
    KOSTL: "Cost Center",
    LTEXT: "Service Description",
    SAKTO: "G/L Account",
    AUFNR: "Internal Order",
    WERKS: "Plant",
    LWERT: "Gross Value",
    CRDAT: "Creation Date",
    CRTIM: "Creation Time",
    EMP_ID: "Employee ID",
    EXTROW: "Service Row",
    KTEXT1: "Short Text",
    NETWR: "Net Value",
};

const formatHeader = (key: string) => {
    if (columnMapping[key]) {
        return columnMapping[key];
    }
    return key
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, (c) => c.toUpperCase());
};

export default function SrProcessTable({ data = [] }: PrProcessTableProps) {
    if (!data || data.length === 0) {
        return (
            <Box
                p={8}
                textAlign="center"
                color="gray.500"
                borderWidth="1px"
                borderRadius="lg"
                bg="gray.50"
                borderStyle="dashed"
            >
                <Text fontSize="lg">No line items available</Text>
            </Box>
        );
    }

    // Extract headers from the first item
    const headers = Object.keys(data[0]);

    return (
        <Box
            borderWidth="1px"
            borderRadius="xl"
            overflow="hidden"
            boxShadow="sm"
            bg="white"
        >
            <Box overflowX="auto"
                sx={{
                    "&::-webkit-scrollbar": { height: "8px" },
                    "&::-webkit-scrollbar-track": { bg: "gray.50" },
                    "&::-webkit-scrollbar-thumb": {
                        bg: "gray.300",
                        borderRadius: "full",
                        "&:hover": { bg: "gray.400" },
                    },
                }}
            >
                <Table variant="simple" size="sm">
                    <Thead bg="gray.100">
                        <Tr>
                            {headers.map((header) => (
                                <Th
                                    key={header}
                                    whiteSpace="nowrap"
                                    py={3}
                                    textTransform="uppercase"
                                    fontSize="xs"
                                    letterSpacing="wider"
                                    color="gray.600"
                                >
                                    {formatHeader(header)}
                                </Th>
                            ))}
                        </Tr>
                    </Thead>
                    <Tbody>
                        {data.map((item, rowIndex) => (
                            <Tr
                                key={rowIndex}
                                _hover={{ bg: "gray.50" }}
                                transition="background 0.2s"
                            >
                                {headers.map((header) => {
                                    const value = item[header];
                                    return (
                                        <Td
                                            key={`${rowIndex}-${header}`}
                                            whiteSpace="nowrap"
                                            py={2}
                                            fontSize="sm"
                                            color="gray.700"
                                        >
                                            {header === "STATUS" ? (
                                                <Badge
                                                    colorScheme={
                                                        value === "APPROVED"
                                                            ? "green"
                                                            : value === "PENDING"
                                                                ? "yellow"
                                                                : value === "REJECTED"
                                                                    ? "red"
                                                                    : "gray"
                                                    }
                                                    variant="subtle"
                                                    px={2}
                                                    py={0.5}
                                                    borderRadius="full"
                                                >
                                                    {value}
                                                </Badge>
                                            ) : (
                                                value
                                            )}
                                        </Td>
                                    );
                                })}
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
            </Box>
            <Box p={2} bg="gray.50" borderTopWidth="1px">
                <Text fontSize="xs" color="gray.500" textAlign="right">
                    Total Items: {data.length}
                </Text>
            </Box>
        </Box>
    );
}
